// BarcodeQuickEnroll — быстрый скан карты клиента в верхней панели (правки владельца):
// поле с автофокусом; скан штрих-карты (или ввод ФИО/телефона) + Enter → поиск
// клиента (в т.ч. по номеру карты) → окно со всеми занятиями сегодня.
//
// Скан ОТМЕЧАЕТ ПРИХОД, а не записывает (правка владельца 2026-07-29): человек
// стоит на ресепшене с картой в руке, и ожидание от сканера — «визит засчитан».
// Раньше здесь была запись (статус «Ожидание»), поэтому посещение не считалось
// и абонемент не списывался — менеджер об этом узнавал только из отчётов.
//
// Клиент мог записаться заранее (мини-апп, админ) — тогда строка посещения уже
// есть, и POST /attendance упрётся в UNIQUE. В этом случае переводим
// существующую запись в «Пришёл» тем же путём, что и карточка занятия
// (PATCH /attendance/:id/correct) — списание там тоже происходит.
import { useMutation } from '@tanstack/react-query';
import { ScanLine } from 'lucide-react';
import { useRef, useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { markAttendance } from '@/features/clients/api/attendanceActions.api';
import { ApiError, errorText } from '@/lib/api';
import { formatTime } from '@/lib/format';
import { useDefaultFormBranchId } from '@/lib/tenant-context';
import { useIsMobile } from '@/lib/use-media';

import { searchClients, type ClientLookupItem } from '../../cash/api/lookups.api';
import { listEnrollments, markVisit, type OccurrenceItem } from '../api/schedule.api';
import { useOccurrences } from '../hooks/useSchedule';

/** Диапазон «сегодня» [00:00; +1 день) в ISO — для запроса занятий дня. */
function todayRange(): { from: string; to: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(start.getTime() + 24 * 3600 * 1000);
  return { from: start.toISOString(), to: end.toISOString() };
}

function clientName(c: ClientLookupItem): string {
  return [c.lastName, c.firstName].filter(Boolean).join(' ') || 'Клиент';
}

export function BarcodeQuickEnroll(): JSX.Element {
  // Запись со сканера — действие в КОНКРЕТНОМ филиале (план 106): когда в
  // шапке отмечено несколько, филиал не подставляем.
  const branchId = useDefaultFormBranchId();
  // Клавиатура на телефоне не должна открываться сама (план 110).
  const isMobile = useIsMobile();
  const inputRef = useRef<HTMLInputElement>(null);
  const [term, setTerm] = useState('');
  const [client, setClient] = useState<ClientLookupItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const q = term.trim();
    if (!q || searching) return;
    setSearching(true);
    setError(null);
    try {
      // Объединённые карточки не предлагаем (план 70).
      const results = await searchClients(q, branchId ?? undefined, true);
      // Представителя не записываем — берём учащегося; иначе первого найденного.
      const student = results.find((c) => c.clientType !== 'representative') ?? results[0];
      if (!student) setError('Клиент не найден (карта не привязана?)');
      else setClient(student);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSearching(false);
    }
  }

  function close(): void {
    setClient(null);
    setTerm('');
    setError(null);
    // Возврат курсора в поле — тоже только на десктопе: на телефоне это снова
    // подняло бы клавиатуру, уже после закрытия окна записи.
    if (!isMobile) inputRef.current?.focus();
  }

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className="inline-flex h-9 items-center gap-2 rounded-full bg-whisper-white px-4 text-sm text-ink-black focus-within:ring-2 focus-within:ring-blueprint-blue/40"
        title="Отсканируйте карту клиента или введите ФИО / телефон / номер карты"
      >
        <ScanLine className="h-4 w-4 shrink-0 text-muted-text" />
        {/* autoFocus — только на десктопе: курсор в поле скана экономит движение
            на ресепшене. На телефоне тот же автофокус выкидывает клавиатуру на
            пол-экрана при каждом открытии CRM, а сканера там нет вовсе
            (жалоба владельца 28.08, план 110). */}
        <input
          ref={inputRef}
          autoFocus={!isMobile}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Скан карты / поиск…"
          aria-label="Скан карты клиента"
          className="w-40 bg-transparent font-medium text-ink-black outline-none placeholder:text-muted-text max-lg:w-24"
        />
      </form>
      {error ? <span className="text-xs text-destructive">{error}</span> : null}
      {client ? (
        <QuickEnrollModal client={client} {...(branchId ? { branchId } : {})} onClose={close} />
      ) : null}
    </>
  );
}

function QuickEnrollModal({
  client,
  branchId,
  onClose,
}: {
  client: ClientLookupItem;
  branchId?: string;
  onClose: () => void;
}): JSX.Element {
  const range = todayRange();
  const occ = useOccurrences({ ...range, ...(branchId ? { branchIds: [branchId] } : {}) });
  const [done, setDone] = useState<string | null>(null);
  const mark = useMutation({
    mutationFn: async (occurrenceId: string) => {
      try {
        await markAttendance({
          client_id: client.id,
          occurrence_id: occurrenceId,
          status: 'attended',
        });
        return;
      } catch (e) {
        // Клиент уже записан (мини-апп / админ) — строка посещения есть,
        // вставить вторую нельзя. Переводим существующую в «Пришёл».
        if (!(e instanceof ApiError) || e.code !== 'attendance.duplicate') throw e;
      }
      const rows = await listEnrollments(occurrenceId);
      const row = rows.find((r) => r.client_id === client.id);
      if (!row) throw new Error('Запись на занятие не найдена — отметьте в карточке занятия');
      if (row.status === 'attended') return; // уже отмечен, повторный скан безвреден
      await markVisit(row.attendance_id, 'attended');
    },
  });
  // Скрываем отменённые/перенесённые — записывать в них нельзя.
  const items = (occ.data?.items ?? []).filter(
    (o) => o.status !== 'cancelled' && o.status !== 'moved',
  );

  function markInto(o: OccurrenceItem): void {
    if (mark.isPending) return;
    mark.mutate(o.id, {
      onSuccess: () => setDone(`${o.groupName} в ${formatTime(o.scheduledAt)}`),
    });
  }

  return (
    <Modal open onClose={onClose} size="md" title={`Отметить приход: ${clientName(client)}`}>
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {done ? (
          <div className="space-y-3">
            <p className="text-sm">
              Отмечен на «<span className="font-medium">{done}</span>».
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setDone(null)}>
                Отметить ещё
              </Button>
              <Button size="sm" onClick={onClose}>
                Готово
              </Button>
            </div>
          </div>
        ) : occ.isLoading ? (
          <p className="text-sm text-muted-foreground">Загрузка занятий…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">На сегодня занятий нет.</p>
        ) : (
          <>
            <p className="mb-2 text-sm text-muted-foreground">
              Выберите занятие — посещение засчитается и спишется с абонемента:
            </p>
            <ul className="space-y-2">
              {items.map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    disabled={mark.isPending}
                    onClick={() => markInto(o)}
                    className="flex w-full items-center justify-between gap-2 rounded-card border border-divider-gray px-3 py-2 text-left hover:bg-whisper-white disabled:opacity-50"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-ink-black">
                        {o.groupName}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatTime(o.scheduledAt)} · {o.teacherName || '—'} · {o.enrolledCount}/
                        {o.maxAttendees}
                      </span>
                    </span>
                    <span className="shrink-0 text-xs font-medium text-primary">Отметить →</span>
                  </button>
                </li>
              ))}
            </ul>
            {mark.isError ? (
              <p className="mt-2 text-sm text-destructive">{errorText(mark.error)}</p>
            ) : null}
          </>
        )}
      </div>
    </Modal>
  );
}
